/**
 * ContactManagementSection - Manage customer emails and phones
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { User, Envelope, Phone, Plus, BellRinging, BellSlash, Trash, CircleNotch, Check, WarningCircle, ChatSlash, ArrowsMerge } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { SectionHeader, SectionContent } from './shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/use-toast';
import {
  getCustomerEmails,
  addCustomerEmail,
  deleteCustomerEmail,
  getCustomerPhones,
  addCustomerPhone,
  updateCustomerPhoneType,
  deleteCustomerPhone,
  toggleCustomerPrimaryContact,
  updateCustomerContactOwner,
  patchCustomer,
} from '@/lib/api/routes/customers';
import type { ContactOwnerUpdate } from '@/lib/api/routes/customers';
import { InlineEditName } from '@/components/ui/inline-edit-name';
import { queryKeys } from '@/lib/queryKeys';
import { ContactRole, PhoneType, isValidPhoneType } from '@ruta/shared';
import { getE164PhoneNumber, isValidPhoneNumber, formatPhoneNumber } from '@/components/ui/phone-input';
import type { CustomerEntity } from './types';

interface ContactManagementSectionProps {
  entity: CustomerEntity;
  onCustomerUpdate?: (customer: CustomerEntity) => void;
}

type ContactType = 'email' | 'phone';

// Role display labels for "someone new" (excludes CUSTOMER since that's handled by "for them")
const NEW_CONTACT_ROLES: { value: ContactRole; label: string }[] = [
  { value: ContactRole.SPOUSE, label: 'Spouse' },
  { value: ContactRole.MANAGER, label: 'Property Manager' },
  { value: ContactRole.TENANT, label: 'Tenant' },
  { value: ContactRole.BILLING, label: 'Billing Contact' },
  { value: ContactRole.OTHER, label: 'Other' },
];

export const ROLE_LABELS: Record<string, string> = {
  CUSTOMER: 'Customer',
  SPOUSE: 'Spouse',
  MANAGER: 'Property Manager',
  TENANT: 'Tenant',
  BILLING: 'Billing',
  OTHER: 'Other',
};

export const PHONE_TYPE_OPTIONS: { value: PhoneType; label: string }[] = [
  { value: PhoneType.MOBILE, label: 'Mobile' },
  { value: PhoneType.OFFICE, label: 'Office' },
  { value: PhoneType.HOME, label: 'Home' },
];

export function PhoneTypeSelect({
  value,
  onChange,
  disabled,
  triggerClassName,
}: {
  value?: PhoneType;
  onChange: (value: PhoneType) => void;
  disabled?: boolean;
  triggerClassName?: string;
}) {
  return (
    <Select value={value ?? ''} onValueChange={(v) => onChange(v as PhoneType)} disabled={disabled}>
      <SelectTrigger className={triggerClassName}>
        <SelectValue placeholder="Set type" />
      </SelectTrigger>
      <SelectContent>
        {PHONE_TYPE_OPTIONS.map(({ value: v, label }) => (
          <SelectItem key={v} value={v}>{label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface PersonGroup {
  key: string;
  firstName: string;
  lastName: string;
  role: string;
  isPrimary: boolean;
  contacts: Array<{ type: 'email' | 'phone'; value: string; isPrimary: boolean; isVerified: boolean; id: string; smsOptedOut?: boolean; phoneType?: PhoneType }>;
}

function groupContactsByPerson(
  emails: import('@/lib/api/routes/customers').CustomerEmail[],
  phones: import('@/lib/api/routes/customers').CustomerPhone[],
): PersonGroup[] {
  const groups = new Map<string, PersonGroup>();

  for (const email of emails) {
    const firstName = email.firstName || '';
    const lastName = email.lastName || '';
    const role = email.role || 'CUSTOMER';
    const key = `${firstName.toLowerCase()}|${lastName.toLowerCase()}|${role}`;

    if (!groups.has(key)) {
      groups.set(key, { key, firstName, lastName, role, isPrimary: false, contacts: [] });
    }
    const group = groups.get(key)!;
    group.contacts.push({ type: 'email', value: email.email, isPrimary: email.isPrimary, isVerified: email.isVerified, id: email.id });
    if (email.isPrimary) group.isPrimary = true;
  }

  for (const phone of phones) {
    const firstName = phone.firstName || '';
    const lastName = phone.lastName || '';
    const role = phone.role || 'CUSTOMER';
    const key = `${firstName.toLowerCase()}|${lastName.toLowerCase()}|${role}`;

    if (!groups.has(key)) {
      groups.set(key, { key, firstName, lastName, role, isPrimary: false, contacts: [] });
    }
    const group = groups.get(key)!;
    group.contacts.push({ type: 'phone', value: phone.phone, isPrimary: phone.isPrimary, isVerified: phone.isVerified, id: phone.id, smsOptedOut: phone.smsOptedOut, phoneType: phone.phoneType });
    if (phone.isPrimary) group.isPrimary = true;
  }

  // Sort contacts within each group: emails before phones, then alphabetically by value
  for (const group of groups.values()) {
    group.contacts.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'email' ? -1 : 1;
      return a.value.localeCompare(b.value);
    });
  }

  return Array.from(groups.values()).sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    return a.firstName.localeCompare(b.firstName);
  });
}

export function ContactManagementSection({
  entity,
  onCustomerUpdate,
}: ContactManagementSectionProps) {
  const queryClient = useQueryClient();
  const [addingType, setAddingType] = useState<ContactType | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ type: ContactType; value: string } | null>(null);

  // Merge-duplicate-people state. `mergeKeepKey` is the person to keep; the
  // other person's contact methods are reassigned to them.
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeKeepKey, setMergeKeepKey] = useState<string>('');
  const [mergeInKey, setMergeInKey] = useState<string>('');

  // Form state for adding contacts.
  // contactFor is 'them' (account holder), 'new' (a brand-new person), or
  // the key of an existing non-account-holder person group.
  const [contactFor, setContactFor] = useState<string>('them');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<ContactRole>(ContactRole.SPOUSE);
  // Phone type is an explicit choice — no silent default. Starts unselected.
  const [phoneType, setPhoneType] = useState<PhoneType | undefined>(undefined);
  const [contactValue, setContactValue] = useState('');

  // Fetch customer emails
  const { data: emailsData, isLoading: emailsLoading } = useQuery({
    queryKey: ['customer-emails', entity.id],
    queryFn: () => getCustomerEmails(entity.id),
    enabled: !!entity.id,
  });

  // Fetch customer phones
  const { data: phonesData, isLoading: phonesLoading } = useQuery({
    queryKey: ['customer-phones', entity.id],
    queryFn: () => getCustomerPhones(entity.id),
    enabled: !!entity.id,
  });

  // Add email mutation
  const addEmailMutation = useMutation({
    mutationFn: (data: { email: string; firstName: string; lastName?: string; role?: ContactRole }) =>
      addCustomerEmail(entity.id, data),
    onSuccess: (newEmailRecord) => {
      queryClient.invalidateQueries({ queryKey: ['customer-emails', entity.id] });
      toast({ title: 'Email added', description: `${newEmailRecord.email} has been added.` });
      closeAddDialog();
      if (onCustomerUpdate && !entity.email) {
        onCustomerUpdate({ ...entity, email: newEmailRecord.email });
      }
    },
    onError: (error: any) => {
      toast({ title: 'Failed to add email', description: error.message || 'Please try again.', variant: 'destructive' });
    },
  });

  // Add phone mutation
  const addPhoneMutation = useMutation({
    mutationFn: (data: { phone: string; firstName: string; lastName?: string; role?: ContactRole; phoneType?: PhoneType }) =>
      addCustomerPhone(entity.id, data),
    onSuccess: (newPhoneRecord) => {
      queryClient.invalidateQueries({ queryKey: ['customer-phones', entity.id] });
      toast({ title: 'Phone added', description: `${newPhoneRecord.phone} has been added.` });
      closeAddDialog();
      if (onCustomerUpdate && !entity.phone) {
        onCustomerUpdate({ ...entity, phone: newPhoneRecord.phone });
      }
    },
    onError: (error: any) => {
      toast({ title: 'Failed to add phone', description: error.message || 'Please try again.', variant: 'destructive' });
    },
  });

  // Update phone type mutation
  const updatePhoneTypeMutation = useMutation({
    mutationFn: ({ phone, phoneType }: { phone: string; phoneType: PhoneType }) =>
      updateCustomerPhoneType(entity.id, phone, phoneType),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customer-phones', entity.id] });
      toast({
        title: data.phoneType === PhoneType.OFFICE ? 'Marked as office' : 'Marked as mobile',
        description: data.phoneType === PhoneType.OFFICE
          ? 'Office numbers will not receive text messages.'
          : 'Mobile numbers can receive text messages.',
      });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to update phone type', description: error.message || 'Please try again.', variant: 'destructive' });
    },
  });

  // Delete email mutation
  const deleteEmailMutation = useMutation({
    mutationFn: ({ email }: { email: string }) => deleteCustomerEmail(entity.id, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-emails', entity.id] });
      toast({ title: 'Email removed' });
      setDeleteTarget(null);
    },
    onError: (error: any) => {
      toast({ title: 'Failed to remove email', description: error.message || 'Please try again.', variant: 'destructive' });
      setDeleteTarget(null);
    },
  });

  // Delete phone mutation
  const deletePhoneMutation = useMutation({
    mutationFn: ({ phone }: { phone: string }) => deleteCustomerPhone(entity.id, phone),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-phones', entity.id] });
      toast({ title: 'Phone removed' });
      setDeleteTarget(null);
    },
    onError: (error: any) => {
      toast({ title: 'Failed to remove phone', description: error.message || 'Please try again.', variant: 'destructive' });
      setDeleteTarget(null);
    },
  });

  // Toggle primary contact mutation
  const setPrimaryMutation = useMutation({
    mutationFn: ({ contactType, contactValue }: { contactType: 'email' | 'phone'; contactValue: string }) =>
      toggleCustomerPrimaryContact(entity.id, contactType, contactValue),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customer-emails', entity.id] });
      queryClient.invalidateQueries({ queryKey: ['customer-phones', entity.id] });

      const isEmail = data.toggledContact.contactType === 'EMAIL';
      const displayVal = isEmail ? data.toggledContact.contactValue : formatPhoneNumber(data.toggledContact.contactValue);
      toast({
        title: data.toggledContact.isPrimary ? 'Notifications enabled' : 'Notifications disabled',
        description: data.toggledContact.isPrimary
          ? `${displayVal} will now receive notifications.`
          : `${displayVal} will no longer receive notifications.`,
      });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to set primary contact', description: error.message || 'Please try again.', variant: 'destructive' });
    },
  });

  // Merge two people into one. Every contact method owned by the "merge in"
  // person is reassigned to the "keep" person's identity (name + role). Each
  // method keeps its own verification and notification status, so a primary
  // number/email on either person stays primary after the merge. Conversation
  // history lives at the customer level and is untouched.
  const mergeMutation = useMutation({
    mutationFn: async ({ keep, mergeIn }: { keep: PersonGroup; mergeIn: PersonGroup }) => {
      const updates: ContactOwnerUpdate[] = mergeIn.contacts.map((c) => ({
        contactType: c.type,
        contactValue: c.value,
        firstName: keep.firstName,
        lastName: keep.lastName || undefined,
        role: keep.role as ContactRole,
      }));
      await updateCustomerContactOwner(entity.id, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-emails', entity.id] });
      queryClient.invalidateQueries({ queryKey: ['customer-phones', entity.id] });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.detail(entity.id) });
      toast({ title: 'Contacts merged' });
      closeMergeDialog();
    },
    onError: (error: any) => {
      toast({ title: 'Failed to merge contacts', description: error.message || 'Please try again.', variant: 'destructive' });
    },
  });

  // Parse displayName into firstName/lastName for "them" option
  const parseDisplayName = (displayName: string) => {
    const parts = displayName.trim().split(' ');
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: undefined };
    }
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  };

  const closeAddDialog = () => {
    setAddingType(null);
    setContactFor('them');
    setFirstName('');
    setLastName('');
    setRole(ContactRole.SPOUSE);
    setPhoneType(undefined);
    setContactValue('');
  };

  const closeMergeDialog = () => {
    setMergeOpen(false);
    setMergeKeepKey('');
    setMergeInKey('');
  };

  const openAddDialog = (type: ContactType) => {
    setAddingType(type);
    setContactFor('them');
    setFirstName('');
    setLastName('');
    setRole(ContactRole.SPOUSE);
    setPhoneType(undefined);
    setContactValue('');
  };

  const handleAdd = () => {
    if (!contactValue.trim() || !addingType) return;

    // Determine the person info
    let personFirstName: string;
    let personLastName: string | undefined;
    let personRole: ContactRole;

    if (contactFor === 'them') {
      const parsed = parseDisplayName(entity.displayName);
      personFirstName = parsed.firstName;
      personLastName = parsed.lastName;
      personRole = ContactRole.CUSTOMER;
    } else if (contactFor === 'new') {
      if (!firstName.trim()) {
        toast({ title: 'First name required', description: 'Please enter a first name for this contact.', variant: 'destructive' });
        return;
      }
      personFirstName = firstName.trim();
      personLastName = lastName.trim() || undefined;
      personRole = role;
    } else {
      const existing = existingOtherPeople.find((g) => g.key === contactFor);
      if (!existing) {
        toast({ title: 'Please select who this is for', variant: 'destructive' });
        return;
      }
      personFirstName = existing.firstName;
      personLastName = existing.lastName || undefined;
      personRole = existing.role as ContactRole;
    }

    if (addingType === 'email') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactValue.trim())) {
        toast({ title: 'Invalid email', description: 'Please enter a valid email address.', variant: 'destructive' });
        return;
      }
      addEmailMutation.mutate({
        email: contactValue.trim(),
        firstName: personFirstName,
        lastName: personLastName,
        role: personRole,
      });
    } else {
      const e164Phone = getE164PhoneNumber(contactValue);
      if (!isValidPhoneNumber(e164Phone)) {
        toast({ title: 'Invalid phone', description: 'Please enter a valid phone number.', variant: 'destructive' });
        return;
      }
      if (!isValidPhoneType(phoneType)) {
        toast({ title: 'Number type required', description: 'Please choose whether this is a mobile or office number.', variant: 'destructive' });
        return;
      }
      addPhoneMutation.mutate({
        phone: e164Phone,
        firstName: personFirstName,
        lastName: personLastName,
        role: personRole,
        phoneType,
      });
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'email') {
      deleteEmailMutation.mutate({ email: deleteTarget.value });
    } else {
      deletePhoneMutation.mutate({ phone: deleteTarget.value });
    }
  };

  const emails = emailsData?.emails || [];
  const phones = phonesData?.phones || [];
  const isLoading = emailsLoading || phonesLoading;
  const isAdding = addEmailMutation.isPending || addPhoneMutation.isPending;
  const isDeleting = deleteEmailMutation.isPending || deletePhoneMutation.isPending;

  // Total contacts from database records only
  const totalContacts = emails.length + phones.length;
  const canManageContacts = totalContacts > 1;

  const personGroups = useMemo(() => groupContactsByPerson(emails, phones), [emails, phones]);
  const hasMultiplePeople = personGroups.length > 1;
  const existingOtherPeople = useMemo(
    () => personGroups.filter((g) => g.role !== 'CUSTOMER'),
    [personGroups],
  );

  const personLabel = (p: PersonGroup) => {
    const name = `${p.firstName}${p.lastName ? ` ${p.lastName}` : ''}`.trim() || 'Unnamed';
    const roleLabel = ROLE_LABELS[p.role] || p.role;
    return `${name} (${roleLabel})`;
  };

  const mergeKeep = personGroups.find((g) => g.key === mergeKeepKey);
  const mergeIn = personGroups.find((g) => g.key === mergeInKey);
  const isMerging = mergeMutation.isPending;

  const openMergeDialog = () => {
    // Default to keeping the account holder (or the first person) and merging
    // the next distinct person into them.
    const keep = personGroups.find((g) => g.role === 'CUSTOMER') || personGroups[0];
    const mergeInto = personGroups.find((g) => g.key !== keep?.key);
    setMergeKeepKey(keep?.key || '');
    setMergeInKey(mergeInto?.key || '');
    setMergeOpen(true);
  };

  const handleMerge = () => {
    if (!mergeKeep || !mergeIn || mergeKeep.key === mergeIn.key) return;
    mergeMutation.mutate({ keep: mergeKeep, mergeIn });
  };

  return (
    <div className="space-y-3">
      <SectionHeader
        icon={User}
        title="Contact"
        action={
          <div className="flex items-center gap-1">
            {/* Merge duplicate people — only when the customer has more than one */}
            {hasMultiplePeople && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex items-center gap-1"
                title="Merge duplicate contacts"
                onClick={openMergeDialog}
              >
                <ArrowsMerge className="h-3.5 w-3.5" />
                <span className="text-[11px]">Merge</span>
              </Button>
            )}

            {/* Add Email Button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex items-center gap-1"
              title="Add email"
              onClick={() => openAddDialog('email')}
            >
              <Envelope className="h-3.5 w-3.5" />
              <Plus className="h-3 w-3" />
            </Button>

            {/* Add Phone Button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex items-center gap-1"
              title="Add phone"
              onClick={() => openAddDialog('phone')}
            >
              <Phone className="h-3.5 w-3.5" />
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        }
      />

      {/* Add Contact Dialog */}
      <Dialog open={!!addingType} onOpenChange={(open) => !open && closeAddDialog()}>
        <DialogContent className="sm:max-w-[500px] p-0 gap-0 overflow-hidden">
          <DialogHeader variant="bordered">
            <DialogTitle className="flex items-center gap-2">
              {addingType === 'email' ? <Envelope className="h-5 w-5" /> : <Phone className="h-5 w-5" />}
              Add {addingType === 'email' ? 'Email Address' : 'Phone Number'}
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            <p className="text-sm text-muted-foreground">
              Who does this {addingType === 'email' ? 'email' : 'phone number'} belong to?
            </p>
            {/* Contact owner selection */}
            <div className="space-y-2">
              <Label>This is for</Label>
              <Select value={contactFor} onValueChange={setContactFor}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="them">{entity.displayName} (this customer)</SelectItem>
                  {existingOtherPeople.map((person) => {
                    const name = `${person.firstName}${person.lastName ? ` ${person.lastName}` : ''}`.trim();
                    const roleLabel = ROLE_LABELS[person.role] || person.role;
                    return (
                      <SelectItem key={person.key} value={person.key}>
                        {name} ({roleLabel})
                      </SelectItem>
                    );
                  })}
                  <SelectItem value="new">Someone else</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* New person fields */}
            {contactFor === 'new' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={isAdding}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={isAdding}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={role} onValueChange={(v) => setRole(v as ContactRole)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {NEW_CONTACT_ROLES.map(({ value, label }) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Contact value input */}
            <div className="space-y-2">
              <Label htmlFor="contactValue">{addingType === 'email' ? 'Email Address' : 'Phone Number'} *</Label>
              {addingType === 'email' ? (
                <Input
                  id="contactValue"
                  type="email"
                  placeholder="email@example.com"
                  value={contactValue}
                  onChange={(e) => setContactValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  disabled={isAdding}
                />
              ) : (
                <PhoneInput
                  placeholder="(555) 123-4567"
                  value={contactValue}
                  onChange={setContactValue}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  disabled={isAdding}
                />
              )}
            </div>

            {/* Phone type (controls SMS eligibility) — explicit choice required */}
            {addingType === 'phone' && (
              <div className="space-y-2">
                <Label>Number Type *</Label>
                <PhoneTypeSelect value={phoneType} onChange={setPhoneType} />
                <p className="text-[11px] text-muted-foreground">
                  Choose mobile or office. Office numbers won't receive text messages.
                </p>
              </div>
            )}
          </DialogBody>

          <DialogFooter variant="bordered">
            <Button variant="ghost" onClick={closeAddDialog} disabled={isAdding}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!contactValue.trim() || (contactFor === 'new' && !firstName.trim()) || (addingType === 'phone' && !isValidPhoneType(phoneType)) || isAdding}
            >
              {isAdding && <CircleNotch className="h-4 w-4 animate-spin mr-2" />}
              Add {addingType === 'email' ? 'Email' : 'Phone'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Duplicates Dialog */}
      <Dialog open={mergeOpen} onOpenChange={(open) => !open && closeMergeDialog()}>
        <DialogContent className="sm:max-w-[500px] p-0 gap-0 overflow-hidden">
          <DialogHeader variant="bordered">
            <DialogTitle className="flex items-center gap-2">
              <ArrowsMerge className="h-5 w-5" />
              Merge Contacts
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            <p className="text-sm text-muted-foreground">
              Combine two duplicate contacts into one. All phone numbers and emails move onto the contact you keep — nothing is lost.
            </p>

            <div className="space-y-2">
              <Label>Keep this contact</Label>
              <Select value={mergeKeepKey} onValueChange={setMergeKeepKey}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a contact" />
                </SelectTrigger>
                <SelectContent>
                  {personGroups.map((p) => (
                    <SelectItem key={p.key} value={p.key} disabled={p.key === mergeInKey}>
                      {personLabel(p)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Merge in (this contact is removed)</Label>
              <Select value={mergeInKey} onValueChange={setMergeInKey}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a contact" />
                </SelectTrigger>
                <SelectContent>
                  {personGroups.map((p) => (
                    <SelectItem key={p.key} value={p.key} disabled={p.key === mergeKeepKey}>
                      {personLabel(p)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Preview of the merged result */}
            {mergeKeep && mergeIn && mergeKeep.key !== mergeIn.key && (
              <div className="rounded-md border border-stone-200 bg-stone-50 p-3 space-y-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">
                  After merge — {personLabel(mergeKeep)} will have
                </p>
                {[...mergeKeep.contacts, ...mergeIn.contacts].map((c) => (
                  <div key={`${c.type}-${c.value}`} className="flex items-center gap-2">
                    {c.type === 'email' ? (
                      <Envelope className="h-3.5 w-3.5 text-stone-400 flex-shrink-0" />
                    ) : (
                      <Phone className="h-3.5 w-3.5 text-stone-400 flex-shrink-0" />
                    )}
                    <span className="text-sm text-stone-700 truncate">
                      {c.type === 'phone' ? formatPhoneNumber(c.value) : c.value}
                    </span>
                    {c.isPrimary && (
                      <span className="flex items-center gap-0.5 text-[10px] text-green-600">
                        <BellRinging className="h-2.5 w-2.5" />
                        Notify
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </DialogBody>

          <DialogFooter variant="bordered">
            <Button variant="ghost" onClick={closeMergeDialog} disabled={isMerging}>
              Cancel
            </Button>
            <Button
              onClick={handleMerge}
              disabled={!mergeKeep || !mergeIn || mergeKeep.key === mergeIn.key || isMerging}
            >
              {isMerging && <CircleNotch className="h-4 w-4 animate-spin mr-2" />}
              Merge Contacts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SectionContent>
        <div className="space-y-2">
        <div className="flex items-center gap-2 pb-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Name</span>
          <InlineEditName
            firstName={entity.firstName ?? ''}
            lastName={entity.lastName ?? ''}
            className="w-72"
            successMessage="Customer name updated"
            onSave={async ({ firstName, lastName }) => {
              await patchCustomer(entity.id, { firstName, lastName });
              queryClient.invalidateQueries({ queryKey: queryKeys.customers.detail(entity.id) });
              queryClient.invalidateQueries({ queryKey: ['customer-emails', entity.id] });
              queryClient.invalidateQueries({ queryKey: ['customer-phones', entity.id] });
            }}
          >
            <span className="inline-flex items-center gap-1.5">
              <span className="text-sm font-medium text-stone-800">{entity.displayName}</span>
              <span className="text-xs font-semibold text-orange-500 group-hover/edit:underline">edit</span>
            </span>
          </InlineEditName>
        </div>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CircleNotch className="h-3.5 w-3.5 animate-spin" />
            <span>Loading...</span>
          </div>
        ) : (
          <>
            {personGroups.map((group, groupIdx) => (
              <div key={group.key}>
                {/* Person header — only show when multiple people */}
                {hasMultiplePeople && (
                  <div className={`flex items-center gap-1.5 ${groupIdx > 0 ? 'mt-3 pt-3 border-t border-stone-100' : ''}`}>
                    <span className="text-xs font-medium text-stone-700">
                      {group.firstName}{group.lastName ? ` ${group.lastName}` : ''}
                    </span>
                    {group.role !== 'CUSTOMER' && (
                      <span className="text-[10px] text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded-full">
                        {ROLE_LABELS[group.role] || group.role}
                      </span>
                    )}
                  </div>
                )}

                {/* Contact rows for this person */}
                {group.contacts.map((contact) => (
                  <ContactRow
                    key={contact.id}
                    type={contact.type}
                    value={contact.value}
                    isPrimary={contact.isPrimary}
                    isVerified={contact.isVerified}
                    smsOptedOut={contact.smsOptedOut}
                    phoneType={contact.phoneType}
                    onChangePhoneType={(t) => updatePhoneTypeMutation.mutate({ phone: contact.value, phoneType: t })}
                    isChangingPhoneType={updatePhoneTypeMutation.isPending}
                    onSetPrimary={() => setPrimaryMutation.mutate({ contactType: contact.type, contactValue: contact.value })}
                    onDelete={() => setDeleteTarget({ type: contact.type, value: contact.value })}
                    isSettingPrimary={setPrimaryMutation.isPending}
                    canSetPrimary={canManageContacts}
                  />
                ))}
              </div>
            ))}

            {/* Empty state */}
            {totalContacts === 0 && (
              <p className="text-xs text-muted-foreground italic">No contact information</p>
            )}
          </>
        )}
        </div>
      </SectionContent>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleteTarget?.type === 'email' ? 'Email Address' : 'Phone Number'}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{deleteTarget?.type === 'phone' ? formatPhoneNumber(deleteTarget?.value || '') : deleteTarget?.value}</strong> from this customer?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-red-600 hover:bg-red-700">
              {isDeleting ? <CircleNotch className="h-4 w-4 animate-spin mr-2" /> : <Trash className="h-4 w-4 mr-2" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Helper component for contact rows
function ContactRow({
  type,
  value,
  isPrimary,
  isVerified,
  smsOptedOut,
  phoneType,
  onChangePhoneType,
  isChangingPhoneType,
  onSetPrimary,
  onDelete,
  isSettingPrimary,
  canSetPrimary = false,
}: {
  type: 'email' | 'phone';
  value: string;
  isPrimary: boolean;
  isVerified: boolean;
  smsOptedOut?: boolean;
  phoneType?: PhoneType;
  onChangePhoneType?: (phoneType: PhoneType) => void;
  isChangingPhoneType?: boolean;
  onSetPrimary?: () => void;
  onDelete?: () => void;
  isSettingPrimary?: boolean;
  canSetPrimary?: boolean;
}) {
  const Icon = type === 'email' ? Envelope : Phone;
  const displayValue = type === 'phone' ? formatPhoneNumber(value) : value;
  const isOfficePhone = type === 'phone' && phoneType === PhoneType.OFFICE;

  return (
    <div className="group flex items-center gap-2 py-1.5 px-2 -mx-2 rounded-md hover:bg-muted/50 transition-colors">
      <Icon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
      <p className="text-sm text-foreground flex-1 min-w-0 truncate">{displayValue}</p>
      {type === 'phone' && onChangePhoneType && (
        <PhoneTypeSelect
          value={phoneType}
          onChange={onChangePhoneType}
          disabled={isChangingPhoneType}
          triggerClassName="h-6 w-[84px] px-2 py-0 text-[11px] text-stone-600 shadow-none"
        />
      )}
      {type === 'phone' && smsOptedOut && (
        <span className="flex items-center gap-0.5 text-[10px] text-red-500" title="Opted out of SMS (texted STOP)">
          <ChatSlash className="h-2.5 w-2.5" />
          SMS off
        </span>
      )}
      {isVerified ? (
        <span className="flex items-center gap-0.5 text-[10px] text-green-600">
          <Check className="h-2.5 w-2.5" />
          Verified
        </span>
      ) : (
        <span className="flex items-center gap-0.5 text-[10px] text-stone-400">
          <WarningCircle className="h-2.5 w-2.5" />
          Unverified
        </span>
      )}
      <div className="flex items-center gap-1">
        <button
          type="button"
          className={`inline-flex items-center h-6 px-1.5 gap-0.5 text-[10px] transition-colors ${
            isOfficePhone
              ? "text-stone-400 opacity-40 cursor-default"
              : isPrimary
              ? canSetPrimary
                ? "text-green-600 hover:text-green-700 cursor-pointer"
                : "text-green-600"
              : canSetPrimary
                ? "text-stone-400 hover:text-stone-600 cursor-pointer"
                : "text-stone-400 opacity-50 cursor-default"
          }`}
          onClick={!isOfficePhone && canSetPrimary ? onSetPrimary : undefined}
          disabled={isSettingPrimary || isOfficePhone}
          title={isOfficePhone ? "Office numbers don't receive texts" : isPrimary ? "Receiving notifications — click to disable" : "Not receiving notifications — click to enable"}
        >
          {isSettingPrimary ? (
            <CircleNotch className="h-2.5 w-2.5 animate-spin" />
          ) : isPrimary ? (
            <BellRinging className="h-2.5 w-2.5" />
          ) : (
            <BellSlash className="h-2.5 w-2.5" />
          )}
          Notify
        </button>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-stone-400 hover:text-red-500"
          onClick={onDelete}
          title="Remove"
        >
          <Trash className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
